import { IPagedResponse } from '../apiInterfaces';
import { IShipmentListItem } from '../logistics';
import { IOrderMin } from '../order';
import { DealerActionKeys as keys } from './dealerActionKeys';
import * as types from './dealerActionTypes';
import {
  IDealer,
  IDealerFinancialSummary,
  IDealerMin,
  IDealerOrderApiRequest,
  IDealerShipmentsRequest,
  IPowerAssessment,
  IPreseasonApiResponse,
  IPreseasonGoalUpdateRequest,
  IPreseasonGoalUpdateResponse,
  IPreseasonTargetUpdateObj,
  IPreseasonTargetUpdateRequest
} from './dealerInterfaces';
import { DealerLoadingTypes } from './dealerReducer';

export const dealerActionCreators = {
  addPowerAssessment: (payload: IPowerAssessment): types.AddDealerPowerAssessment => ({
    payload,
    type: keys.ADD_DEALER_POWER_ASSESSMENT
  }),
  addShipmentIds: (payload: { accountNum: string; shipmentIds: number[] }): types.AddShipmentIds => ({
    payload,
    type: keys.ADD_DEALER_SHIPMENT_IDS
  }),
  fetchDealer: (accountNum: string): types.FetchDealer => ({ payload: accountNum, type: keys.FETCH_DEALER }),
  fetchDealerFulfilled: (dealer: IDealer): types.FetchDealerFulfilled => ({
    payload: dealer,
    type: keys.FETCH_DEALER_FULFILLED
  }),
  fetchDealerList: (
    type: 'local' | 'global' = 'global',
    showFullPageLoading: boolean = false
  ): types.FetchDealerList => ({
    payload: { type, showFullPageLoading },
    type: keys.FETCH_DEALER_LIST
  }),
  fetchDealerListFulfilled: (payload: { dealers: IDealerMin[]; full?: boolean }): types.FetchDealerListFulfilled => ({
    payload,
    type: keys.FETCH_DEALER_LIST_FULFILLED
  }),
  fetchDealerListRejected: (): types.FetchDealerListRejected => ({
    error: true,
    payload: '',
    type: keys.FETCH_DEALER_LIST_REJECTED
  }),
  fetchDealerRejected: (accountNum: string): types.FetchDealerRejected => ({
    error: true,
    payload: accountNum,
    type: keys.FETCH_DEALER_REJECTED
  }),
  fetchFinancials: (accountNum: string): types.FetchDealerFinancials => ({
    payload: accountNum,
    type: keys.FETCH_DEALER_FINANCIALS
  }),
  fetchOpenOrders: (accountNum: string) =>
    ({
      payload: { accountNum },
      type: keys.FETCH_DEALER_OPEN_ORDERS
    } as const),
  fetchOrders: (payload: IDealerOrderApiRequest): types.FetchDealerOrders => ({
    payload,
    type: keys.FETCH_DEALER_ORDERS
  }),
  fetchOrdersFulfilled: (
    accountNum: string,
    response: IPagedResponse<IOrderMin>
  ): types.FetchDealerOrdersFulfilled => ({
    payload: { accountNum, response },
    type: keys.FETCH_DEALER_ORDERS_FULFILLED
  }),
  fetchOrdersRejected: (accountNum: string): types.FetchDealerOrdersRejected => ({
    error: true,
    payload: accountNum,
    type: keys.FETCH_DEALER_ORDERS_REJECTED
  }),
  fetchPowerAssessments: (accountNum: string): types.FetchDealerPowerAssessments => ({
    payload: { accountNum },
    type: keys.FETCH_DEALER_POWER_ASSESSMENTS
  }),
  fetchPreseason: (payload: {
    accountNum: string;
    fiscalYear: string;
    includePrograms?: boolean;
  }): types.FetchDealerPreSeason => ({
    payload,
    type: keys.FETCH_DEALER_PRESEASON
  }),
  fetchShipments: (request: IDealerShipmentsRequest): types.FetchDealerShipments => ({
    payload: request,
    type: keys.FETCH_DEALER_SHIPMENTS
  }),
  setFinancials: (financials: IDealerFinancialSummary[]): types.SetDealerFinancials => ({
    payload: financials,
    type: keys.SET_DEALER_FINANCIALS
  }),
  setOpenOrders: (payload: { accountNum: string; amount: number }) =>
    ({
      payload,
      type: keys.SET_DEALER_OPEN_ORDERS
    } as const),
  setPowerAssessments: (payload: IPowerAssessment[]): types.SetDealerPowerAssessments => ({
    payload,
    type: keys.SET_DEALER_POWER_ASSESSMENTS
  }),
  setPreseason: (payload: IPreseasonApiResponse): types.SetDealerPreSeason => ({
    payload,
    type: keys.SET_DEALER_PRESEASON
  }),
  setPreseasonGoal: (payload: IPreseasonGoalUpdateResponse): types.SetPreseasonGoal => ({
    payload,
    type: keys.SET_PRESEASON_GOAL
  }),
  setPreseasonTarget: (payload: IPreseasonTargetUpdateObj): types.SetPreseasonTarget => ({
    payload,
    type: keys.SET_PRESEASON_PROGRAM
  }),
  setShipments: (accountNum: string, response: IPagedResponse<IShipmentListItem>): types.SetDealerShipments => ({
    payload: { accountNum, response },
    type: keys.SET_DEALER_SHIPMENTS
  }),
  toggleListLoading: (): types.ToggleDealerListLoading => ({ type: keys.TOGGLE_DEALER_LIST_LOADING }),
  toggleLoading: (payload: { accountNum: string; type: DealerLoadingTypes }): types.ToggleDealerLoading => ({
    payload,
    type: keys.TOGGLE_DEALER_LOADING
  }),
  updatePreseasonGoal: (payload: IPreseasonGoalUpdateRequest): types.UpdatePreseasonGoal => ({
    payload,
    type: keys.UPDATE_PRESEASON_TARGET
  }),
  updatePreseasonTarget: (payload: IPreseasonTargetUpdateRequest): types.UpdatePreseasonTarget => ({
    payload,
    type: keys.UPDATE_DEALER_PROGRAM
  })
};
